import { useEffect, useRef } from "react";
import { renderAsync } from "docx-preview";

interface DocxViewerProps {
    src: string;
    height?: string;
}

/**
 * Lightweight DOCX renderer that converts the file to HTML using `docx-preview` and
 * injects it into a div. Falls back silently if the document cannot be rendered.
 */
export const DocxViewer = ({ src, height = "600px" }: DocxViewerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const renderDocx = async () => {
            if (!src || !containerRef.current) return;
            try {
                const response = await fetch(src);
                if (!response.ok) {
                    // eslint-disable-next-line no-console
                    console.error(`Failed to fetch docx: ${response.status} ${response.statusText}`);
                    return;
                }
                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();
                // Clear previous content
                containerRef.current.innerHTML = "";
                await renderAsync(arrayBuffer, containerRef.current, undefined, {
                    // Make the output basic-style friendly
                    inWrapper: true,
                    ignoreWidth: false,
                    ignoreHeight: false,
                    className: "docx"
                });
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Error rendering docx", err);
            }
        };

        renderDocx();
        // Cleanup when src changes/unmounts
        return () => {
            if (containerRef.current) {
                containerRef.current.innerHTML = "";
            }
        };
    }, [src]);

    return <div ref={containerRef} style={{ height, overflow: "auto" }} />;
};
